import { useAppSelector } from "app/hooks";
import { AppType } from "constants/AppType";
import getAdminsPath from "helpers/getAdminsPath";
import { useNavigate } from "react-router";
import { RoutePermissions } from "constants/RoutePermissions";
import usePermissions from "amnShared/Permissions/usePermissions";
import useApproval from "amnPos/Approvals/useApproval";
import { PermissionType } from "amnShared/Permissions/IPermissions";
import { GetNeededApproval } from "amnPos/Approvals/approvalHelper";
import { IAskSupervisorApproval } from "amnPos/Approvals/IApprovals";

const useAmnNavigate = () => {
  const navigate = useNavigate();
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const user = useAppSelector((state) => state.user);
  const { canEmployee } = usePermissions();
  const { AskSupervisorApproval } = useApproval();

  const getOrganizationPath = (path: string) => {
    const organization =
      user.organizationId !== "" ? user.organizationId : ":organizationId";
    return `/${organization}${path}`;
  };

  const getNavigationPath = (
    pathname: string,
    appType: AppType = AppType.Organizations,
  ) => {
    switch (appType) {
      case AppType.Admins:
        return getAdminsPath(pathname);
      case AppType.Organizations:
        return getOrganizationPath(pathname);
    }
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const amnNavigate = (
    pathname: any,
    appType = AppType.Organizations,
    navigateOptions?: any,
    permissionAddisionalInfo?: IAskSupervisorApproval,
  ) => {
    // 1. Resolve target path
    const modifiedPathname = getNavigationPath(pathname, appType);

    // 2. Find matching permission
    // We strip the organizationId to check against the registry (logical path).
    const pathParts = modifiedPathname.split("/").filter(Boolean);
    const logicalPath = `/${pathParts.slice(1).join("/")}`;

    // Try direct match first
    let requiredPermission = RoutePermissions[logicalPath];

    // If not found, try replacing the last part with :id (common pattern)
    if (!requiredPermission) {
      // e.g., /pos/shiftSummary/guid-123 -> /pos/shiftSummary/:id
      const dynamicPath = pathParts.slice(1, -1).join("/");
      const potentialDynamicRoute = `/${dynamicPath}/:id`;
      requiredPermission = RoutePermissions[potentialDynamicRoute];
    }

    // If still not found, we could try replacing the 3rd part or implementing more complex matching
    // But for now, replacing the last segment covers most cases in RoutePermissions.ts.
    // If we have deeper nesting like /a/:id/b/:id2, we might need a more general loop,

    // 3. Check permission if found
    if (requiredPermission) {
      const check = canEmployee(requiredPermission);
      if (!check.response) {
        // Permission denied -> Ask Supervisor
        const neededApproval = GetNeededApproval(check.error);

        AskSupervisorApproval(
          neededApproval,
          (approval) => {
            // Approved! Navigate with "approved" state
            navigate(modifiedPathname, {
              ...navigateOptions,
              state: {
                ...navigateOptions?.state,
                approved: true, // Pass explicit approval
              },
            });
          },
          () => {
            // Denied or Cancelled - do nothing
          },
          permissionAddisionalInfo ?? {},
        );
        return;
      }
    }

    // 4. Default Navigation (Permission OK or No Permission Required)
    navigate(modifiedPathname, navigateOptions);
  };

  return { amnNavigate, getNavigationPath };
};

export default useAmnNavigate;
